import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import { FadeUp } from "@/components/landing/cinematic";

/* ---- Tiny authentic stage visuals ---- */

function OnboardViz() {
    return (
        <div className="w-full rounded-lg border border-[#1c1917]/10 bg-[#faf7f0] p-2.5 text-[10px]">
            <div className="flex items-center justify-between">
                <span className="font-medium text-[#1c1917]">Global Fasteners Pvt</span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 text-[8.5px] font-semibold text-emerald-700">96%</span>
            </div>
            <div className="mt-1.5 flex gap-1">
                {["Steel", "6d lead", "GST ✓"].map((t) => (
                    <span key={t} className="rounded bg-[#efe8dc] px-1.5 py-0.5 font-mono text-[8.5px] text-[#59524a]">{t}</span>
                ))}
            </div>
        </div>
    );
}

function ListViz() {
    return (
        <div className="grid w-full grid-cols-4 gap-1.5">
            {["M10", "PVC", "PPE", "PLY", "BRK", "PNT", "CEM", "+2k"].map((s, i) => (
                <div
                    key={s}
                    className={`flex h-8 items-center justify-center rounded-md border font-mono text-[8.5px] ${
                        i === 7 ? "border-dashed border-[#1c1917]/20 text-[#a29a8e]" : "border-[#1c1917]/10 bg-[#faf7f0] text-[#44403c]"
                    }`}
                >
                    {s}
                </div>
            ))}
        </div>
    );
}

function ReceiveViz() {
    return (
        <div className="flex w-full items-center gap-2">
            <div className="flex-1 rounded-lg border border-[#1c1917]/10 bg-[#faf7f0] p-2 text-center">
                <div className="font-mono text-[9px] font-semibold text-[#1c1917]">A-01-01</div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#e8e0d0]">
                    <div className="h-full w-3/4 bg-emerald-500" />
                </div>
            </div>
            <span className="font-mono text-[10px] text-[#c2410c]">←</span>
            <div className="rounded-md border border-[#1c1917]/10 bg-white px-2 py-1.5 font-mono text-[8.5px] text-[#59524a]">
                GRN-1041
            </div>
        </div>
    );
}

function SellViz() {
    return (
        <div className="w-full space-y-1.5 text-[9.5px]">
            <div className="flex justify-between rounded-md border border-[#1c1917]/10 bg-[#faf7f0] px-2 py-1.5">
                <span className="text-[#44403c]">PVC Pipe 25mm × 6m</span>
                <span className="font-mono text-[#1c1917]">× 120</span>
            </div>
            <div className="flex justify-between rounded-md border border-[#1c1917]/10 bg-[#faf7f0] px-2 py-1.5">
                <span className="text-[#44403c]">Hex Bolt M10</span>
                <span className="font-mono text-[#1c1917]">× 400</span>
            </div>
            <div className="text-right font-mono text-[8.5px] text-[#a29a8e]">SO-2088 · Kohli Hardware</div>
        </div>
    );
}

function InvoiceViz() {
    return (
        <div className="mx-auto w-28 rounded-lg border border-[#1c1917]/10 bg-white p-2.5 shadow-sm">
            <div className="font-mono text-[8.5px] text-[#a29a8e]">INV-2026-311</div>
            <div className="mt-1.5 space-y-1">
                <div className="h-1.5 w-4/5 rounded bg-[#efe8dc]" />
                <div className="h-1.5 w-3/5 rounded bg-[#efe8dc]" />
            </div>
            <div className="mt-2 flex justify-between border-t border-[#1c1917]/[0.07] pt-1.5 text-[9px]">
                <span className="text-[#59524a]">Total</span>
                <span className="font-semibold text-[#1c1917]">₹86,140</span>
            </div>
        </div>
    );
}

function CollectViz() {
    return (
        <div className="w-full space-y-1.5 text-[9px]">
            {[
                { m: "UPI", pct: 62 },
                { m: "Bank", pct: 28 },
                { m: "Cash", pct: 10 },
            ].map((r) => (
                <div key={r.m} className="flex items-center gap-2">
                    <span className="w-8 font-mono text-[#59524a]">{r.m}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#e8e0d0]">
                        <div className="h-full bg-[#1c1917]" style={{ width: `${r.pct}%` }} />
                    </div>
                </div>
            ))}
            <div className="text-right font-mono text-[8.5px] text-emerald-600">₹1.52L collected today</div>
        </div>
    );
}

function AnalyseViz() {
    return (
        <div className="flex h-14 w-full items-end gap-1">
            {[30, 45, 38, 55, 48, 68, 62, 80].map((h, i) => (
                <div key={i} className="flex-1 rounded-t-sm bg-[#1c1917]" style={{ height: `${h}%`, opacity: 0.35 + i * 0.08 }} />
            ))}
        </div>
    );
}

function DecideViz() {
    return (
        <div className="w-full space-y-1.5">
            <div className="ml-auto w-fit rounded-lg rounded-tr-sm bg-[#1c1917] px-2 py-1 text-[9px] text-white">
                What should I reorder?
            </div>
            <div className="flex w-fit items-center gap-1.5 rounded-lg rounded-tl-sm border border-[#4353e4]/30 bg-[#4353e4]/[0.07] px-2 py-1 text-[9px] text-[#1c1917]">
                <Sparkles className="h-2.5 w-2.5 text-[#4353e4]" strokeWidth={2} />
                200 × OPC Cement · 4-day runway
            </div>
        </div>
    );
}

const STAGES = [
    { module: "Supplier Brain", title: "Onboard", viz: <OnboardViz /> },
    { module: "Product Brain", title: "List", viz: <ListViz /> },
    { module: "Warehouse Hub", title: "Receive", viz: <ReceiveViz /> },
    { module: "Sales Hub", title: "Sell", viz: <SellViz /> },
    { module: "Billing", title: "Invoice", viz: <InvoiceViz /> },
    { module: "Finance Hub", title: "Collect", viz: <CollectViz /> },
    { module: "Business Intelligence", title: "Analyse", viz: <AnalyseViz /> },
    { module: "YugOS Intelligence", title: "Decide", viz: <DecideViz />, ai: true },
];

function StageCard({ p, i, stage }) {
    const n = STAGES.length;
    const t = 0.1 + (i / n) * 0.8;
    const active = useTransform(p, [t - 0.09, t], [0, 1]);
    const opacity = useTransform(active, [0, 1], [0.45, 1]);
    const scale = useTransform(active, [0, 1], [0.96, 1]);
    const barScale = active;

    return (
        <motion.div
            data-testid={`flow-stage-${stage.title.toLowerCase()}`}
            style={{ opacity, scale }}
            className={`relative w-[290px] shrink-0 rounded-2xl border bg-white p-5 shadow-[0_24px_60px_-28px_rgba(28,25,23,0.3)] ${
                stage.ai ? "border-[#4353e4]/40" : "border-[#1c1917]/10"
            }`}
        >
            <motion.div
                className={`absolute inset-x-5 top-0 h-[2px] origin-left rounded-full ${stage.ai ? "bg-[#4353e4]" : "bg-[#c2410c]"}`}
                style={{ scaleX: barScale }}
            />
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a29a8e]">
                    {String(i + 1).padStart(2, "0")}
                </span>
                {stage.ai && (
                    <span className="rounded-full border border-[#4353e4]/30 bg-[#4353e4]/[0.07] px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest text-[#4353e4]">
                        AI
                    </span>
                )}
            </div>
            <div className="mt-4 flex min-h-[92px] items-center">{stage.viz}</div>
            <div className="mt-4 border-t border-[#1c1917]/[0.07] pt-3">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#a29a8e]">{stage.module}</div>
                <div className="mt-0.5 font-display text-[19px] font-bold tracking-tight text-[#1c1917]">{stage.title}</div>
            </div>
        </motion.div>
    );
}

export default function Flow() {
    const ref = useRef(null);
    const { scrollYProgress: p } = useScroll({ target: ref });
    const x = useTransform(p, [0.06, 0.94], ["2%", "-62%"]);

    return (
        <section id="flow" className="relative bg-[#efe8dc]" data-testid="flow-section">
            {/* Desktop — scroll-driven horizontal journey */}
            <div ref={ref} className="relative hidden lg:block h-[320vh]">
                <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
                    <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
                        <div className="font-mono text-[11px] font-medium uppercase tracking-[0.26em] text-[#c2410c]">
                            End-to-end
                        </div>
                        <h2 className="mt-4 font-display text-4xl lg:text-[54px] font-bold tracking-[-0.03em] text-[#1c1917] leading-[1.04]">
                            Supplier to intelligence.{" "}
                            <span className="font-editorial font-medium text-[#59524a]">One flow.</span>
                        </h2>
                    </div>

                    <div className="relative mt-14">
                        {/* Connector baseline with traveling pulse */}
                        <svg className="absolute left-0 right-0 top-1/2 h-[2px] w-full" aria-hidden="true">
                            <line x1="0" y1="1" x2="100%" y2="1" stroke="rgba(28,25,23,0.12)" strokeWidth="1.5" strokeDasharray="4 8" className="animate-dash" />
                        </svg>
                        <motion.div style={{ x }} className="relative flex w-max items-stretch gap-7 pl-[max(1.5rem,calc((100vw-80rem)/2+2rem))]">
                            {STAGES.map((s, i) => (
                                <StageCard key={s.title} p={p} i={i} stage={s} />
                            ))}
                        </motion.div>
                    </div>

                    <div className="mx-auto mt-12 w-full max-w-7xl px-6 lg:px-8">
                        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#59524a]">
                            POs → GRNs → Inventory → Orders → Invoices → Payments → <span className="text-[#4353e4]">YugOS Intelligence</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile — stacked journey */}
            <div className="lg:hidden px-5 sm:px-6 py-20">
                <FadeUp>
                    <div className="font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-[#c2410c]">
                        End-to-end
                    </div>
                    <h2 className="mt-4 font-display text-[32px] font-bold tracking-[-0.03em] text-[#1c1917] leading-[1.06]">
                        Supplier to intelligence.{" "}
                        <span className="font-editorial font-medium text-[#59524a]">One flow.</span>
                    </h2>
                </FadeUp>
                <div className="mt-8 space-y-4">
                    {STAGES.map((s, i) => (
                        <FadeUp key={s.title} delay={Math.min(i * 0.04, 0.24)}>
                            <div
                                className={`rounded-2xl border bg-white p-4 sm:p-5 ${s.ai ? "border-[#4353e4]/40" : "border-[#1c1917]/10"}`}
                                data-testid={`flow-stage-mobile-${s.title.toLowerCase()}`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#a29a8e] truncate">
                                        {String(i + 1).padStart(2, "0")} · {s.module}
                                    </span>
                                    {s.ai && (
                                        <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-widest text-[#4353e4]">AI</span>
                                    )}
                                </div>
                                <div className="mt-3 flex min-h-[68px] items-center">{s.viz}</div>
                                <div className="mt-3 font-display text-[17px] sm:text-[18px] font-bold tracking-tight text-[#1c1917]">
                                    {s.title}
                                </div>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}
